import {
  getHeaderValue,
  COMMON_CONSTANTS,
  getParsedCrudData
} from '@nile/components';
import { AccountClient } from '@nile/graphql';
import { GET_COLLECTIONS } from '@nile/rmm/src/modules/knowledge-base/graphql';
import { add, isEmpty, reverse } from 'lodash';

const tempFileKey = 'tempFile';

function purifyTags(htmlString) {
  return htmlString.replace(/<img([^>]+)>/g, (match, attributes) => {
    if (!/align=/.test(attributes)) {
      attributes += ` align="center"`;
    }
    return `<div style="text-align: center" class="image"><img${attributes} /></div>`;
  });
}
async function loadArticle(url, token) {
  const requestOptions = {
    method: 'GET',
    credentials: 'include',
    headers: token
      ? {
          ...getHeaderValue(),
          authorization: `Bearer nl-${token}`
        }
      : getHeaderValue()
  };
  const res = await fetch(url, requestOptions);
  if (res?.status === 200) {
    const articleContent = await res.text();
    return articleContent;
  }
  return null;
}

const getParentHierarchy = (node) => {
  const items = [];
  while (node) {
    const { itemId, name } = node;
    items.push({ itemId, name });
    node = node.parent;
  }
  return reverse(items);
};

const convertToHierarchy = (items) => {
  return items.reduce(
    (acc, curr) => ({
      itemId: curr.itemId,
      name: curr.name,
      parent: acc
    }),
    undefined
  );
};

function fetchCollections({ listInfo }) {
  return AccountClient.query({
    query: GET_COLLECTIONS,
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
    variables: {
      listInfo: {
        page: listInfo?.page || 1,
        pageSize: listInfo?.pageSize || 500,
        sort: listInfo?.sort || null,
        condition: listInfo?.condition || null
      }
    }
  }).then((res) => {
    const collections = res?.data?.getKbItems || {};
    return {
      collections: collections?.items || [],
      listInfo: collections?.listInfo || null
    };
  });
}

const visibilityMappings = {
  REQUESTER: {
    ALL: {
      clientSharedType: 'AllClients',
      siteSharedType: 'AllSites',
      userRoleSharedType: 'AllRoles',
      portalType: 'REQUESTER'
    },
    SPECIFIC: {
      clientSharedType: 'Client',
      siteSharedType: 'Site',
      userRoleSharedType: 'Role',
      portalType: 'REQUESTER'
    },
    NONE: {
      clientSharedType: null,
      siteSharedType: null,
      userRoleSharedType: null,
      userSharedType: null,
      groupSharedType: null,
      portalType: 'REQUESTER'
    }
  },
  TECHNICIAN: {
    ALL: {
      userSharedType: 'AllUsers',
      groupSharedType: 'AllGroups',
      portalType: 'TECHNICIAN'
    },
    SPECIFIC: {
      userSharedType: 'AllUsers',
      groupSharedType: 'Group',
      portalType: 'TECHNICIAN'
    },
    ONLY_ME: {
      userSharedType: 'User',
      groupSharedType: 'AllGroups',
      portalType: 'TECHNICIAN'
    }
  }
};

function getVisibiltyObject(val, key) {
  const portalMappings = visibilityMappings[key];
  if (!portalMappings) return null;
  const sharedConfig = portalMappings[val] || {};
  return { portalType: key, ...sharedConfig, sharedType: val };
}

function getValFromVisibilityObject(obj) {
  if (!obj || !obj.portalType) return { val: 'NONE', sharedType: 'NONE' };
  const { portalType, ...sharedProps } = obj;
  const portalMappings = visibilityMappings[portalType];
  for (const [val, config] of Object.entries(portalMappings)) {
    const isMatch = Object.keys(config).every(
      (key) => config[key] === obj[key]
    );
    if (isMatch) {
      return { key: portalType, val };
    }
  }
  return { key: portalType, val: 'SPECIFIC' };
}

const getUpdateDifferencePayload = (
  base = [],
  updateAssetList = [],
  isITProduct = false
) => {
  const updatedData = updateAssetList;
  const ALL_CLIENTS_TYPE = 'AllClients';
  const ALL_SITES_TYPE = 'AllSites';
  const ALL_ROLES_SHARED_TYPE = 'AllRoles';
  let {
    addedItems: added,
    deletedItems: deleted,
    updatedItems: updated
  } = getParsedCrudData(base, updatedData, 'mappingId');
  let isAllClientAdded = false;
  const updatedDetails = [];
  if (added.length) {
    added = added.map((value) => {
      if (
        isITProduct
          ? value.siteSharedType === ALL_SITES_TYPE
          : value.clientSharedType === ALL_CLIENTS_TYPE
      ) {
        isAllClientAdded = true;
      }
      return {
        clientSharedType: value?.client?.accountId
          ? value.clientSharedType
          : ALL_CLIENTS_TYPE,
        siteSharedType: value.site?.id ? value.siteSharedType : ALL_SITES_TYPE,
        userRoleSharedType: value.userRoleSharedType,
        portalType: value.portalType,
        accountId:
          value.clientSharedType === ALL_CLIENTS_TYPE
            ? null
            : value.client?.accountId,
        siteId: value.siteSharedType === ALL_SITES_TYPE ? null : value.site?.id,
        addedRoleIds:
          value.userRoleSharedType === ALL_ROLES_SHARED_TYPE
            ? null
            : value.roles?.map((r) => r.roleId),
        deletedRoleIds: null, // deletedRole id will be always null for 'added' case
        mappingId: null, // for new added items, the mapping id should be null
        userSharedType: value?.userSharedType,
        groupSharedType: value?.groupSharedType,
        addedUserIds: value?.addedUserIds,
        addedGroupIds: value?.addedGroupIds
      };
    });
  }
  if (deleted.length) {
    deleted = deleted.map((value) => {
      return value?.mappingId;
    });
  }
  if (updated.length) {
    // need to calculate the added or deleted props, so recalculate again from updated items
    updated.forEach((updatedClient) => {
      if (updatedClient?.portalType === 'TECHNICIAN') {
        const updateRoleObjDetails = {
          mappingId: updatedClient.mappingId,
          portalType: updatedClient.portalType,
          userSharedType: updatedClient.userSharedType,
          groupSharedType: updatedClient.groupSharedType,
          addedUserIds: updatedClient?.addedUserIds,
          deletedUserIds: updatedClient?.deletedUserIds,
          addedGroupIds: updatedClient?.addedGroupIds,
          deletedGroupIds: updatedClient?.deletedGroupIds
        };
        updatedDetails.push(updateRoleObjDetails);
      } else {
        let isClientUpdated = false;
        const baseClient = base.find(
          (a) => a?.mappingId === updatedClient?.mappingId
        );
        const updateRoleObjDetails = {
          accountId: updatedClient?.client?.accountId,
          siteId: updatedClient?.site?.id,
          clientSharedType: updatedClient?.client?.accountId
            ? updatedClient.clientSharedType
            : ALL_CLIENTS_TYPE,
          siteSharedType: updatedClient.site?.id
            ? updatedClient.siteSharedType
            : ALL_SITES_TYPE,
          userRoleSharedType: updatedClient.userRoleSharedType,
          addedRoleIds: updatedClient.addedRoleIds || null,
          deletedRoleIds: updatedClient.deletedRoleIds || null,
          mappingId: updatedClient.mappingId,
          portalType: updatedClient.portalType,
          userSharedType: updatedClient.userSharedType,
          groupSharedType: updatedClient.groupSharedType,
          addedUserIds: updatedClient?.addedUserIds,
          deletedUserIds: updatedClient?.deletedUserIds,
          addedGroupIds: updatedClient?.addedGroupIds,
          deletedGroupIds: updatedClient?.deletedGroupIds
        };
        const isClientOrSiteChanged = isITProduct
          ? baseClient?.site?.id !== updatedClient?.site?.id
          : baseClient?.client?.accountId !== updatedClient?.client?.accountId;
        if (isClientOrSiteChanged) {
          //  client updated
          const addedClient = {
            ...updateRoleObjDetails,
            addedRoleIds: updatedClient.roles?.map((r) => r.roleId),
            deletedRoleIds: null,
            mappingId: null, // for new added items, the mapping id should be null
            accountId: updatedClient?.client?.accountId || null,
            siteId: updatedClient?.site?.id || null
          };
          added.push(addedClient);
          isClientUpdated = true;
          const updatedSharedType = isITProduct
            ? updatedClient.siteSharedType
            : updatedClient.clientSharedType;
          if (
            updatedSharedType === ALL_CLIENTS_TYPE ||
            updatedSharedType === ALL_SITES_TYPE
          ) {
            isAllClientAdded = true;
          } else {
            deleted.push(baseClient?.mappingId);
          }
        }
        // roles updated
        /* if the client is updated no need to calculate the added/updated roles
         */
        if (!isClientUpdated) {
          const { deletedItems, addedItems } = getParsedCrudData(
            baseClient.roles || [],
            updatedClient.roles || [],
            'roleId'
          );
          if (deletedItems.length) {
            const removedRoleId = deletedItems.map((role) => role.roleId);
            updateRoleObjDetails.deletedRoleIds = removedRoleId;
          }
          if (addedItems.length) {
            const addedRoleIds = addedItems.map((role) => role.roleId);
            updateRoleObjDetails.addedRoleIds = addedRoleIds;
          }
          if (deletedItems.length || addedItems.length) {
            updatedDetails.push(updateRoleObjDetails);
          }
        }
      }
    });
  }
  if (isAllClientAdded) {
    added = added.filter((a) =>
      isITProduct
        ? a.siteSharedType === ALL_SITES_TYPE
        : a.clientSharedType === ALL_CLIENTS_TYPE
    );
    const addedSection = added?.[0];
    /** In the existing mapping if the user selects All clients, then delete all other clients mapping */
    const deletedAllMapping = base
      .map((value) => {
        if (value?.portalType === addedSection?.portalType) {
          return value?.mappingId;
        }
        return null;
      })
      .filter((val) => val);
    if (!isEmpty(deletedAllMapping)) {
      deleted.push(...deletedAllMapping);
    }
  }

  return {
    added: added.length ? added : null,
    updated: updatedDetails.length ? updatedDetails : null,
    deleted: deleted.length ? deleted : null
  };
};

const mergeVisibilityObject = (article) => {
  const data = article?.visibility;
  if (!data) return { ...article, visibility: [] };

  const mergedMap = new Map();

  for (const item of data) {
    const type = item.portalType;

    if (!mergedMap.has(type)) {
      mergedMap.set(type, {
        mappingId: item?.mappingId,
        clientSharedType: item?.clientSharedType,
        siteSharedType: item?.siteSharedType,
        userRoleSharedType: item?.userRoleSharedType,
        userSharedType: item?.userSharedType,
        groupSharedType: item?.groupSharedType,
        client: [],
        site: [],
        roles: [],
        users: [],
        groups: [],
        portalType: type
      });
    }

    const merged = mergedMap.get(type);

    if (item.client) merged.client.push(item.client);
    if (item.site) merged.site.push(item.site);
    if (item.roles) merged.roles.push(...item.roles);
    if (item.users) merged.users.push(...item.users);
    if (item.groups) merged.groups.push(...item.groups);
  }

  const result = Array.from(mergedMap.values());

  return { ...article, visibility: result };
};
export {
  purifyTags,
  loadArticle,
  fetchCollections,
  getParentHierarchy,
  convertToHierarchy,
  getVisibiltyObject,
  getValFromVisibilityObject,
  getUpdateDifferencePayload,
  mergeVisibilityObject
};
